<template>
  <div class="mt-10">
    <ComparisonGroupSelectorModal
      :id="groupSelectorModalId"
      :groups="allGroups"
      :treeifiedGroups="allTreeifiedGroups"
      @update-selected-groups="handleApplyGroupSelection"
    />
    <div v-if="!selectedStatItem" class="card">
      <div class="card-body">
        <div class="table-wrapper">
          <div class="d-flex justify-content-end mb-3">
            <ComparisonContextMenu
              :hasHistory="hasHistory"
              :trend-enabled="shouldShowTrend"
              :sensitive-visible="!hideSensitive"
              @toggle-trend="handleToggleTrend"
              @toggle-sensitive="handleToggleSensitive"
              @open-group-selector="handleOpenGroupSelector"
            />
          </div>
          <div>
            <div
              id="table-header"
              ref="tableHeader"
              class="table-scroll-header syncscroll"
              :class="[{ 'is-stuck': isStuck }]"
              name="synscrollGroup1"
            >
              <PulsTableHeader
                :columns="columns"
                :tableConfig="tableConfig"
                :onClickColumnHeader="handleColumnHeaderClick"
                :expandedCategory="expandedCategory"
              />
            </div>
            <div
              id="table-content"
              class="table-scroll-inner syncscroll"
              name="synscrollGroup1"
            >
              <div
                v-for="surveyWithResults in Object.keys(resultsForSurveys)"
                :key="surveyWithResults"
              >
                <!-- :treeifiedGroups="treeifiedGroups" -->
                <TableGroup
                  :addRowGroup="addRowGroup"
                  :allGroups="groups"
                  :attributes="attributes"
                  :columns="columns"
                  :expandedGroups="expandedGroups"
                  :groups="rowsForSurveys[surveyWithResults]"
                  :loading="loading[surveyWithResults] || {}"
                  :onAddRow="handleAddRow"
                  :onCellClick="handleCellClick"
                  :onExpandGroup="handleExpandGroup"
                  :onSelectGroup="handleSelectGroup"
                  :onRemoveRow="handleRemoveRow"
                  :onLoadMoreGroups="handleLoadMoreGroups"
                  :onSelectAddRowGroup="handleSelectAddRowGroup"
                  :organizationGroup="organizationGroup"
                  :selectedAttributeSegment="selectedAttributeSegment"
                  :shouldShowTrend="shouldShowTrend"
                  :surveyDetails="allSurveyDetails[surveyWithResults]"
                  :surveyName="surveyWithResults"
                  :surveyResults="resultsForSurveys[surveyWithResults]"
                  :tableConfig="tableConfig"
                  :tagSettings="tagSettings"
                  :theme="theme"
                  :userAddedRowsForSurvey="userAddedRows[surveyWithResults]"
                />
              </div>
            </div>
          </div>
          <div
            v-if="customGroupsFilter.length === 0"
            class="ps-3 load-more-button-wrapper"
          >
            <button
              v-if="endPage < allTreeifiedGroups.length"
              class="btn btn-link btn-sm"
              @click="loadMoreGroups"
            >
              <i class="bi bi-arrow-down-short pe-1 fs-5"></i>
              <span>{{ $t('groupsComparison.loadMore') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="selectedStatItem">
      <PulsSkeletonLoader
        v-if="
          questionResultsForSurveyAreLoading ||
          categoriesResultsPerPersonAreLoading ||
          groupTrendResultsForCategoriesAreLoading
        "
        :skeletonInfo="categoriesBarsWidgetSkeleton"
      />
      <PulsSkeletonLoader
        v-else-if="
          tagsResultsPerPersonAreLoading || groupTrendResultsForTagsAreLoading
        "
        :skeletonInfo="tagsDonutsWidgetSkeleton"
      />
      <CategoriesBarsWidget
        v-else-if="
          resultsForSurveys &&
          resultsForSurveys[surveyName] &&
          selectedStatItem &&
          selectedStatItem.type === 'categories'
        "
        id="comparison-categories-bars"
        :data="dataConfigForCategoriesBarsWidget"
        :singleStatItem="{
          group: selectedStatItem.group,
          name: selectedStatItem.name,
          onBack: selectedStatItem.onBack
        }"
        :categoriesResultsPerPerson="
          selectedStatItem.categoriesResultsPerPerson
        "
        :categories="selectedStatItem.categories"
        :questions="selectedStatItem.questions"
        :tags="selectedStatItem.tags"
        :trendCategories="selectedStatItem.trendCategories"
        :trendQuestions="selectedStatItem.trendQuestions"
        :trendTags="selectedStatItem.trendTags"
        :selectedGroup="selectedGroup"
        :surveyName="selectedStatItem.surveyName"
        :surveyDetails="surveyDetails"
        :deregisterWidget="deregisterWidget"
        :userIsGroupAdmin="userIsGroupAdmin"
        :userIsPulsAdmin="userIsPulsAdmin"
        :widgetSliderState="widgetSliderStates['comparison-categories-bars']"
        :updateWidgetSliderState="updateWidgetSliderState"
        :updateWidgetRequests="updateWidgetRequests"
        :userInfo="userInfo"
      />
      <!-- :data="{
          tags: [selectedStatItem.name]
        }" -->
      <TagsDonutsWidget
        v-else-if="
          resultsForSurveys &&
          resultsForSurveys[surveyName] &&
          selectedStatItem &&
          selectedStatItem.type === 'tags'
        "
        id="comparison-tags-donuts"
        :data="dataConfigForTagsDonutsWidget"
        :singleStatItem="{
          group: selectedStatItem.group,
          name: selectedStatItem.name,
          onBack: selectedStatItem.onBack
        }"
        :tagsResultsPerPerson="selectedStatItem.tagsResultsPerPerson"
        :categories="selectedStatItem.categories"
        :questions="{
          ...selectedStatItem.questions,
          ...resultStoreQuestions
        }"
        :tags="selectedStatItem.tags"
        :trendCategories="selectedStatItem.trendCategories"
        :trendQuestions="selectedStatItem.trendQuestions"
        :trendTags="selectedStatItem.trendTags"
        :surveyName="selectedStatItem.surveyName"
        :surveyDetails="surveyDetails"
        :userIsGroupAdmin="userIsGroupAdmin"
        :userIsPulsAdmin="userIsPulsAdmin"
        :deregisterWidget="deregisterWidget"
        :widgetSliderState="widgetSliderStates['comparison-tags-donuts']"
        :updateWidgetSliderState="updateWidgetSliderState"
        :updateWidgetRequests="updateWidgetRequests"
        :userInfo="userInfo"
        class="tag-donuts-comparison"
      />
    </div>
  </div>
</template>

<script>
import TableGroup from './table/TableGroup.vue'
import PulsTableHeader from './table/header/PulsTableHeader.vue'
import PulsSkeletonLoader from '@/components/shared/PulsSkeletonLoader.vue'
import ComparisonContextMenu from './menu/ComparisonContextMenu.vue'
import ComparisonGroupSelectorModal from './modals/ComparisonGroupSelectorModal.vue'
import CategoriesBarsWidget, {
  categoriesBarsWidgetSkeleton
} from '@/components/widgets/categories-bars-widget/CategoriesBarsWidget.vue'
import TagsDonutsWidget, {
  tagsDonutsWidgetSkeleton
} from '@/components/widgets/tags-donuts-widget/TagsDonutsWidget.vue'
import { useComparisonStore } from '@/stores/comparison-store'
import { useStatesStore } from '@/stores/states-store'
import { useSurveysStore } from '@/stores/surveys-store'
import { useResultsStore } from '@/stores/results-store'
import { useWidgetStore } from '@/stores/widget-store'
import {
  buildTreeifiedGroups,
  filterTreeBySelectedGroups
} from '@/helpers/group-tree-helpers'
import { requestTypes } from '@/constants/request-types'
import { resetSyncscrollElements } from '@/helpers/syncscroll.js'
import {
  statItemHasData,
  updateSelectedStatItemOnStoreChange,
  getStatItemsForSelectedStatItem
} from '@/helpers/comparison-helpers'
import { tabTypes } from '@/constants/tab-types'

const initTableConfig = {
  columnWidth: {
    name: {
      'select-group': 30,
      'remove-row': 10,
      title: 260,
      harassment: 30,
      participation: 35,
      'add-row': 30,
      'harassment-dummy': 30
    },
    template: {
      tags: 30,
      categories: 30
    },
    margin: {
      tags: 10
    }
  }
}

export default {
  name: 'Comparison',
  components: {
    PulsTableHeader,
    TableGroup,
    CategoriesBarsWidget,
    TagsDonutsWidget,
    PulsSkeletonLoader,
    ComparisonContextMenu,
    ComparisonGroupSelectorModal
  },
  props: {
    attributes: {
      type: Array,
      default: () => []
    },
    onTabSelected: {
      type: Function,
      required: true
    },
    onSelectGroup: {
      type: Function,
      required: true
    },
    onSelectAttributeSegment: {
      type: Function,
      required: true
    },
    organizationGroup: {
      type: Object,
      default: () => {}
    },
    groups: {
      type: Array,
      default: () => []
    },
    makeDataRequest: {
      type: Function,
      required: true
    },
    surveyDetails: {
      type: Object,
      required: true
    },
    surveyName: {
      type: String,
      required: true
    },
    userIsGroupAdmin: {
      type: Boolean,
      default: false
    },
    selectedTimePeriod: {
      type: String,
      default: ''
    },
    theme: {
      type: String,
      default: 'default'
    },
    userInfo: {
      type: Object,
      default: () => {}
    },
    userIsPulsAdmin: {
      type: Boolean,
      default: false
    },
    selectedGroup: {
      type: Object,
      default: () => {}
    },
    selectedAttributeSegment: {
      type: Object,
      default: () => {}
    }
  },
  setup() {
    const comparisonStore = useComparisonStore()
    const resultsStore = useResultsStore()
    const statesStore = useStatesStore()
    const surveysStore = useSurveysStore()
    const widgetStore = useWidgetStore()

    return {
      comparisonStore,
      resultsStore,
      statesStore,
      surveysStore,
      widgetStore
    }
  },
  data() {
    return {
      addRowGroup: null,
      categoriesBarsWidgetSkeleton: categoriesBarsWidgetSkeleton(),
      tagsDonutsWidgetSkeleton: tagsDonutsWidgetSkeleton({
        colData: {
          tags: ['', '', '']
        }
      }),
      // swiperInstance: null,
      selectedStatItem: null,
      shouldShowTrend: false,
      hideSensitive: false,
      // TODO: determine handling for individual groups
      shouldShowIndividualGroups: true,
      paginationSize: 5,
      expandedGroups: {},
      startPage: 0, // for pagination
      endPage: 0,
      rowsForSurveys: {},
      tableConfig: initTableConfig,
      expandedCategory: null,
      isStuck: false,
      customGroupsFilter: []
    }
  },
  computed: {
    allGroups() {
      const title = this.$tr(this.organizationGroup.messages, 'title')
      const initGroups =
        this.shouldExcludeOrganizationStatistics ||
        !this.userIsAdminForCurrentGroupOrSuperAdmin
          ? []
          : [
              {
                title,
                ...this.organizationGroup,
                externalId: undefined,
                externalParentId: undefined
              }
            ]
      return initGroups.concat(this.groups)
    },
    allSurveyDetails() {
      return {
        [this.surveyName]: this.surveyDetails.details
      }
    },
    groupTrendResultsForTagsAreLoading() {
      return (
        this.selectedStatItem &&
        this.selectedStatItem.type === 'tags' &&
        this.loading[this.surveyName][this.selectedStatItem.group.name] &&
        this.loading[this.surveyName][
          this.selectedStatItem.group.name
        ].includes(this.selectedStatItem.segmentName)
      )
    },
    groupTrendResultsForCategoriesAreLoading() {
      return (
        this.selectedStatItem &&
        this.selectedStatItem.type === 'categories' &&
        this.loading[this.surveyName][this.selectedStatItem.group.name] &&
        this.loading[this.surveyName][
          this.selectedStatItem.group.name
        ].includes(this.selectedStatItem.segmentName)
      )
    },
    questionResultsForSurveyAreLoading() {
      return this.resultsStore.loading[requestTypes.QUESTION_RESULTS_FOR_SURVEY]
    },
    categoriesResultsPerPersonAreLoading() {
      return this.resultsStore.loading[
        requestTypes.CATEGORIES_RESULTS_PER_PERSON
      ]
    },
    tagsResultsPerPersonAreLoading() {
      return this.resultsStore.loading[requestTypes.TAGS_RESULTS_PER_PERSON]
    },
    groupSelectorModalId() {
      return 'comparison-group-selector-modal'
    },
    columns() {
      if (this.details) {
        let result = [
          {
            header: '',
            name: 'remove-row',
            template: 'remove-row'
          },
          {
            header: '',
            name: 'select-group',
            template: 'select-group'
          },
          {
            header: '',
            name: 'title',
            template: 'title'
          },
          {
            header: '',
            name: this.attributes.length ? 'add-row' : 'remove',
            template: this.attributes.length ? 'add-row' : ''
          },
          {
            header: '',
            name: this.shouldShowHarassmentColumn ? 'harassment' : '',
            template: this.shouldShowHarassmentColumn ? 'tags' : ''
          },
          {
            header: '',
            name: !this.shouldShowHarassmentColumn
              ? 'harassment-dummy'
              : 'remove',
            template: ''
          },
          {
            header: '',
            name: this.shouldShowParticipationColumn
              ? 'participation'
              : 'remove',
            template: this.shouldShowParticipationColumn ? 'tags' : ''
          }
        ].filter((col) => col.name !== 'remove')
        const categories = this.details.categories.filter((category) => {
          const categoryIsIncluded =
            !this.excludedCategories ||
            !this.excludedCategories.includes(category.name)

          const categoryHasData = statItemHasData({
            resultsForSurvey: this.resultsForSurveys[this.surveyName],
            type: 'categories',
            name: category.name
          })
          return categoryIsIncluded && categoryHasData
        })
        const messageObj = this.details.messages
        const tags = this.details.tags
          .filter((tagName) => {
            const tagIsIncluded =
              tagName !== 'harassment' && !this.excludedTags.includes(tagName)
            const tagHasData = statItemHasData({
              resultsForSurvey: this.resultsForSurveys[this.surveyName],
              type: 'tags',
              name: tagName
            })
            return tagIsIncluded && tagHasData
          })
          .map((tagName) => {
            return {
              name: tagName,
              header: messageObj.en.tags[tagName]
                ? this.$tr(messageObj, `tags.${tagName}.title`)
                : tagName,
              template: 'tags'
            }
          })

        result = result.concat(tags).concat(
          categories.map((c) => {
            return {
              header: this.$tr(c.details.messages, 'title'),
              name: c.name,
              template: 'categories'
            }
          })
        )
        if (this.expandedCategory) {
          const questions = this.expandedCategory.questions
            .filter((q) => {
              const baseQuestionShouldShow =
                !q.details.precondition &&
                q.details.type !== 'freetext' &&
                !q.details.messages?.en?.ranges
              const isHarassmentQuestion = q.tags?.includes('harassment')
              const shouldHideSensitiveQuestion =
                this.hideSensitive && isHarassmentQuestion
              return baseQuestionShouldShow && !shouldHideSensitiveQuestion
            })
            .map((q) => {
              return {
                header: this.$tr(q.details.messages, 'title'),
                name: q.name,
                template: 'questions'
              }
            })
          result = result.concat(questions)
        }
        return result
      } else {
        return []
      }
    },
    dataConfigForCategoriesBarsWidget() {
      const userType = this.userIsAdminForCurrentGroupOrSuperAdmin
        ? 'admin'
        : 'user'
      return this.details.resultViews[userType].rows.reduce((res, r) => {
        const col = r.cols.find((c) => c.type === 'categories_bars')
        if (col) {
          res = col.data
        }
        return res
      }, {})
    },
    dataConfigForTagsDonutsWidget() {
      if (this.selectedStatItem) {
        const userType = this.userIsAdminForCurrentGroupOrSuperAdmin
          ? 'admin'
          : 'user'
        return this.details.resultViews[userType].rows.reduce((res, r) => {
          const col = r.cols.find(
            (c) =>
              c.type === 'tags_donuts' &&
              c.data.tags?.includes(this.selectedStatItem.name)
          )
          if (col) {
            res = col.data
          }
          return res
        }, {})
      }
      return {}
    },
    details() {
      return this.allSurveyDetails[this.surveyName]
    },
    excludedTags() {
      const defaultExcludedTags = []
      if (this.details && this.details.resultViews.groupComparison) {
        return defaultExcludedTags.concat(
          this.details.resultViews.groupComparison.excludedTags
        )
      } else {
        return defaultExcludedTags
      }
    },
    excludedCategories() {
      if (this.details && this.details.resultViews.groupComparison) {
        return this.details.resultViews.groupComparison.excludedCategories
      } else {
        return []
      }
    },
    hasHistory() {
      return this.surveyDetails?.oldSurveys?.length > 0
    },
    loading() {
      return this.comparisonStore.loading
    },
    tagSettings() {
      if (this.details && this.details.resultViews.groupComparison) {
        return this.details.resultViews.groupComparison.tagSettings
      } else {
        return []
      }
    },
    allTreeifiedGroups() {
      return buildTreeifiedGroups(
        this.allGroups,
        this.shouldShowIndividualGroups,
        this.$tr
      )
    },
    filteredTreeifiedGroups() {
      const selectedGroups =
        this.customGroupsFilter.length > 0
          ? new Set(this.customGroupsFilter)
          : []

      return filterTreeBySelectedGroups(this.allTreeifiedGroups, selectedGroups)
    },
    userAddedRows() {
      return this.comparisonStore.userAddedRows
    },
    userIsAdminForCurrentGroupOrSuperAdmin() {
      return this.userIsGroupAdmin || this.userIsPulsAdmin
    },
    resultsForSurveys() {
      return this.comparisonStore.resultsForSurveys
    },
    resultStoreQuestions() {
      return this.resultsStore.questions
    },
    resultStoreCategoriesResultsPerPerson() {
      return this.resultsStore.categoriesResultsPerPerson
    },
    resultStoreTagsResultsPerPerson() {
      return this.resultsStore.tagsResultsPerPerson
    },
    shouldShowParticipationColumn() {
      return !(this.excludedTags || []).some((t) => t === 'participation')
    },
    shouldShowHarassmentColumn() {
      if (this.hideSensitive) {
        return false
      }
      return statItemHasData({
        resultsForSurvey: this.resultsForSurveys[this.surveyName],
        type: 'tags',
        name: 'harassment'
      })
      // return Object.values(this.resultsForSurveys).find((resultsForSurvey) => {
      //   Object.keys(resultsForSurvey).find((groupName) => {
      //     Object.keys(resultsForSurvey[groupName]).find((segment) => {
      //       if (segment === 'all') {

      //       }
      //     })
      //   })
      // })
    },
    shouldExcludeOrganizationStatistics() {
      return this.details.config?.excludeOrganizationStatistics
    },
    widgetSliderStates() {
      return this.widgetStore.getWidgetSliderStates
    }
  },
  watch: {
    // We used to trigger update on all endPage changes, now we do it manually
    // Remove commented code block once tested
    // endPage() {
    //   this.updateGroupsPagination()
    // },
    resultsForSurveys() {
      if (this.selectedStatItem) {
        const statItems = getStatItemsForSelectedStatItem({
          resultsForSurveys: this.resultsForSurveys,
          allSurveyDetails: this.allSurveyDetails,
          resultStoreCategoriesResultsPerPerson:
            this.resultStoreCategoriesResultsPerPerson,
          resultStoreTagsResultsPerPerson: this.resultStoreTagsResultsPerPerson,
          resultStoreQuestions: this.resultStoreQuestions,
          surveyName: this.surveyName,
          segmentName: this.selectedStatItem.segmentName,
          groupName: this.selectedStatItem.group.name,
          statItemType: this.selectedStatItem.type,
          statItemName: this.selectedStatItem.name
        })
        this.selectedStatItem = {
          ...this.selectedStatItem,
          ...statItems
        }
      }
      this.updateScrollVar()
    },
    resultStoreCategoriesResultsPerPerson(newObj, oldObj) {
      this.selectedStatItem = updateSelectedStatItemOnStoreChange({
        selectedStatItem: this.selectedStatItem,
        target: 'categoriesResultsPerPerson',
        newObj,
        oldObj
      })
      this.updateScrollVar()
    },
    resultStoreTagsResultsPerPerson(newObj, oldObj) {
      this.selectedStatItem = updateSelectedStatItemOnStoreChange({
        selectedStatItem: this.selectedStatItem,
        target: 'tagsResultsPerPerson',
        newObj,
        oldObj
      })
      this.updateScrollVar()
    },
    resultStoreQuestions(newObj, oldObj) {
      this.selectedStatItem = updateSelectedStatItemOnStoreChange({
        selectedStatItem: this.selectedStatItem,
        target: 'questions',
        newObj,
        oldObj
      })
      this.updateScrollVar()
    },
    selectedAttributeSegment() {
      const groupNames = Object.keys(
        this.resultsForSurveys[this.surveyName]
      ).reduce((res, groupName) => {
        // if group is expanded, add its children to the groupnames array
        if (this.expandedGroups[groupName]?.expanded) {
          const children = this.rowsForSurveys[this.surveyName]
            .find((g) => g.name === groupName)
            ?.children.map((c) => c.name)
          res = res.concat(children)
        }
        res.push(groupName)
        return res
      }, [])
      this.comparisonStore.clearAllButCustomRows()
      this.comparisonStore.loadGroupResultsForComparisonAction({
        surveyName: this.surveyName,
        groupNames,
        showTrend: this.shouldShowTrend
      })
    },
    selectedTimePeriod() {
      if (this.shouldShowTrend) {
        this.comparisonStore.updateAllRowsAction({
          surveyName: this.surveyName
        })
      }
    },
    shouldShowTrend() {
      if (this.shouldShowTrend) {
        this.expandedGroups = {}
        const groups = this.rowsForSurveys[this.surveyName]

        // Helper function to get all group names for expanded groups.
        // Move to helper if needed elsewhere
        const collectExpandedGroupNames = (group, result = []) => {
          result.push(group.name)
          if (this.expandedGroups[group.name]?.expanded) {
            group.children.forEach((child) => {
              collectExpandedGroupNames(child, result)
            })
          }

          return result
        }
        const allCustomRowsForSurvey = (
          this.userAddedRows[this.surveyName] || []
        ).map((g) => g.groupName)
        const loadedGroups = allCustomRowsForSurvey.concat(
          groups.reduce((res, group) => {
            return res.concat(collectExpandedGroupNames(group))
          }, [])
        )
        this.comparisonStore.updateAllRowsAction({
          surveyName: this.surveyName,
          groupNames: loadedGroups
        })
      }
    }
  },
  mounted() {
    this.makeDataRequest({
      dataName: 'insightsTeam',
      widgetData: {
        area: 'team',
        numberOfInsightsDetails: 6
      },
      groupName: this.organizationGroup.name
    })
    this.initPagination()
    resetSyncscrollElements()
    this.updateScrollVar()
    window.addEventListener('scroll', this.updateScrollVar, { passive: true })
  },
  unmounted() {
    // cleanup scroll listener
    window.removeEventListener('scroll', this.updateScrollVar)
  },
  methods: {
    handleToggleTrend() {
      if (!this.hasHistory) {
        return
      }
      this.shouldShowTrend = !this.shouldShowTrend
    },
    handleToggleSensitive() {
      this.hideSensitive = !this.hideSensitive
    },
    handleOpenGroupSelector() {
      const event = new Event(`show.puls.modal#${this.groupSelectorModalId}`, {
        bubbles: true
      })
      document.dispatchEvent(event)
    },
    handleApplyGroupSelection(groupNames) {
      if (groupNames.length) {
        this.startPage = 0
        this.endPage = groupNames.length
      } else {
        this.initPagination()
      }
      this.customGroupsFilter = groupNames
      this.recalculateExpandedGroupsPagination()
      this.rowsForSurveys[this.surveyName] = []
      this.updateGroupsPagination()
    },
    getRealChildren(group = {}) {
      return (group.children || []).filter((child) => !child.isCustomUserRow)
    },
    findTreeifiedGroupByName(groupName) {
      const search = (groups) => {
        for (const group of groups) {
          if (group.name === groupName) {
            return group
          }
          if (group.children?.length) {
            const match = search(group.children)
            if (match) {
              return match
            }
          }
        }
        return null
      }
      return search(this.filteredTreeifiedGroups)
    },
    updateExpandedGroupPaginationMeta(
      groupName,
      totalChildren,
      desiredEndPage
    ) {
      if (!this.expandedGroups[groupName]) {
        return
      }
      const safeEndPage = Math.min(desiredEndPage ?? 0, totalChildren ?? 0)
      this.expandedGroups[groupName] = {
        ...this.expandedGroups[groupName],
        endPage: desiredEndPage,
        hasMoreChildren: safeEndPage < (totalChildren ?? 0)
      }
    },
    recalculateExpandedGroupsPagination() {
      Object.entries(this.expandedGroups).forEach(([groupName, state]) => {
        if (!state?.expanded) {
          return
        }
        const fullGroup = this.findTreeifiedGroupByName(groupName)
        if (!fullGroup) {
          return
        }
        const realChildren = this.getRealChildren(fullGroup)
        this.updateExpandedGroupPaginationMeta(
          groupName,
          realChildren.length,
          state.endPage
        )
      })
    },
    updateScrollVar() {
      document.documentElement.style.setProperty(
        '--scroll-y',
        `${window.scrollY}px`
      )
      const el = this.$refs.tableHeader
      if (!el) return

      const isNowStuck = el.getBoundingClientRect().top <= 0
      if (this.isStuck !== isNowStuck) {
        this.isStuck = isNowStuck
      }
    },
    updateGroupsPagination() {
      const newGroupsToLoad =
        this.customGroupsFilter.length > 0
          ? this.filteredTreeifiedGroups
          : this.filteredTreeifiedGroups.slice(this.startPage, this.endPage)
      if (!newGroupsToLoad.length) {
        this.rowsForSurveys[this.surveyName] = []
        return
      }
      const shouldAddToExistingGroups =
        this.customGroupsFilter.length === 0 &&
        this.rowsForSurveys[this.surveyName]
      this.rowsForSurveys[this.surveyName] = shouldAddToExistingGroups
        ? this.rowsForSurveys[this.surveyName].concat(newGroupsToLoad)
        : newGroupsToLoad
      const groupNames = newGroupsToLoad.map((g) => g.name)
      this.comparisonStore.loadGroupResultsForComparisonAction({
        surveyName: this.surveyName,
        groupNames,
        showTrend: this.shouldShowTrend
      })
    },
    // TODO: handle categories bars widget errors and maybe saved slider state
    deregisterWidget({ id, info, groupName }) {},
    updateWidgetRequests({ mode, id, requests }) {
      this.widgetStore.updateWidgetRegistrationAction({
        groupName: this.selectedStatItem.group.name,
        id,
        requests,
        mode
      })
    },
    updateWidgetSliderState({ id, index, info }) {
      const infoCopy = { ...info }
      this.widgetStore.updateWidgetSliderAction({
        id,
        index,
        info: infoCopy
      })
    },
    handleLoadMoreGroups({ group }) {
      if (!group) {
        this.loadMoreGroups()
      } else {
        const realChildren = this.getRealChildren(group)
        // update end page for group
        const desiredEndPage =
          (this.expandedGroups[group.name].endPage || 0) + this.paginationSize
        this.updateExpandedGroupPaginationMeta(
          group.name,
          realChildren.length,
          desiredEndPage
        )

        const childrenToLoad = realChildren
          .map((s) => s.name)
          .slice(0, this.expandedGroups[group.name].endPage)
        const groupsNotLoaded = childrenToLoad.filter((childGroup) => {
          return !this.resultsForSurveys[this.surveyName][childGroup]
        })
        if (groupsNotLoaded.length) {
          this.comparisonStore.loadGroupResultsForComparisonAction({
            surveyName: this.surveyName,
            groupNames: groupsNotLoaded,
            showTrend: this.shouldShowTrend
          })
        }
        // load more children for group
      }
    },
    handleSelectAddRowGroup(addRowGroup) {
      this.addRowGroup = addRowGroup
    },
    async handleAddRow({ surveyName, group, attributeSegment }) {
      if (!this.expandedGroups[group.name]?.expanded) {
        const endPage = this.expandedGroups[group.name]?.endPage || 0
        this.expandedGroups[group.name] = {
          ...(this.expandedGroups[group.name] || {}),
          endPage,
          hasMoreChildren: group.children.length > endPage,
          expanded: true
        }
      }
      if (attributeSegment.level === 0) {
        const allSegmentsInAttribute = this.attributes.filter((a) => {
          return a.attributeName === attributeSegment.name
        })
        for (var i = 0; i < allSegmentsInAttribute.length; i++) {
          const segment = allSegmentsInAttribute[i]
          await this.comparisonStore.addCustomRowToTableAction({
            surveyName,
            rowName: group.name,
            groupName: group.name,
            attributeSegment: segment,
            showTrend: this.shouldShowTrend
          })
        }
      } else {
        this.comparisonStore.addCustomRowToTableAction({
          surveyName,
          rowName: group.name,
          groupName: group.name,
          attributeSegment,
          showTrend: this.shouldShowTrend
        })
      }
    },
    handleColumnHeaderClick(column) {
      if (this.expandedCategory) {
        this.expandedCategory = null
        this.tableConfig = initTableConfig
      } else {
        const category = this.details.categories.find(
          (c) => c.name === column.name
        )
        this.expandedCategory = category
        const questionNamesWidthObject = category.questions.reduce((res, q) => {
          if (!q.details.precondition) {
            res[q.name] = 30
          }
          return res
        }, {})

        this.tableConfig = {
          ...this.tableConfig,
          columnWidth: {
            ...this.tableConfig.columnWidth,
            name: {
              ...this.tableConfig.columnWidth.name,
              ...questionNamesWidthObject,
              [column.name]: 30
            },
            template: {
              ...this.tableConfig.columnWidth.template,
              categories: 0,
              tags: 0
            }
          }
        }
      }
    },
    // getStatItemsForSelectedStatItem moved to helper
    handleCellClick({
      surveyName,
      statItemType,
      statItemName,
      group,
      segmentName = 'all'
    }) {
      const statItems = getStatItemsForSelectedStatItem({
        resultsForSurveys: this.resultsForSurveys,
        allSurveyDetails: this.allSurveyDetails,
        resultStoreCategoriesResultsPerPerson:
          this.resultStoreCategoriesResultsPerPerson,
        resultStoreTagsResultsPerPerson: this.resultStoreTagsResultsPerPerson,
        resultStoreQuestions: this.resultStoreQuestions,
        surveyName,
        segmentName,
        groupName: group.name,
        statItemType,
        statItemName
      })

      this.comparisonStore.loadGroupResultsForComparisonAction({
        surveyName: this.surveyName,
        groupNames: [group.name],
        showTrend: true
      })

      // Set the selected stat item and update UI
      this.selectedStatItem = {
        name: statItemName,
        group,
        segmentName,
        surveyName,
        type: statItemType,
        onBack: () => this.handleSelectedStatItemBack(),
        ...statItems
      }
    },
    handleSelectedStatItemBack() {
      this.widgetStore.clear()
      this.selectedStatItem = null
      this.resultsStore.clearResponsesPerPerson()
      this.resultsStore.clearQuestionResponses()
      // Re-register syncscroll after table DOM is back
      this.$nextTick(() => {
        resetSyncscrollElements()
        this.updateScrollVar()
      })
    },
    handleExpandGroup({ group, shouldLoadChildren = false }) {
      const children = (group.children || []).filter(
        (c) => Boolean(c) && c.name !== 'add-new-row'
      )
      const customRowChildren = children.filter((c) => c.isCustomUserRow)
      const realChildren = children.filter((c) => !c.isCustomUserRow)

      const groupChildrenNames = realChildren.map((c) => c.name)
      if (!this.expandedGroups[group.name]?.expanded) {
        const currentEndPage = this.expandedGroups[group.name]?.endPage || 0
        const childrenToLoad = groupChildrenNames.slice(
          currentEndPage,
          currentEndPage + this.paginationSize
        )
        const endPage =
          this.expandedGroups[group.name]?.endPage ||
          Math.min(childrenToLoad.length, this.paginationSize)
        this.expandedGroups[group.name] = {
          expanded: true,
          endPage,
          hasMoreChildren: endPage < groupChildrenNames.length
        }
        const allChildren = customRowChildren
          .map((c) => c.name)
          .concat(childrenToLoad)
        const groupsNotLoaded = allChildren.filter((childGroup) => {
          return !this.resultsForSurveys[this.surveyName][childGroup]
        })
        if (groupsNotLoaded.length) {
          this.comparisonStore.loadGroupResultsForComparisonAction({
            surveyName: this.surveyName,
            groupNames: groupsNotLoaded,
            showTrend: this.shouldShowTrend
          })
        }
      } else {
        // TODO: we could do this, but we need to get all children recursively and it's maybe not worth it
        // We could also keep group.level with addRowGroup and reset if level is higher
        // if ( ALL groupChildrenNames.includes(this.addRowGroup)) {
        // }
        this.addRowGroup = null
        this.expandedGroups[group.name].expanded = false
      }
    },
    handleSelectGroup({ group }) {
      // TODO: is this too slow?
      // Should we have just one function to make all of these calls to avoid multiple store resets?
      this.onSelectGroup(group)
      this.onSelectAttributeSegment(group.attributeSegment || { name: 'all' })
      this.onTabSelected(tabTypes.RESULTS)
    },
    handleRemoveRow(group) {
      this.comparisonStore.removeRowFromTableAction({
        surveyName: this.surveyName,
        group
      })
    },
    initPagination() {
      this.startPage = 0
      const totalGroups = this.allTreeifiedGroups.length
      if (totalGroups > this.paginationSize) {
        this.endPage = this.paginationSize
      } else {
        this.endPage = totalGroups
      }
      this.updateGroupsPagination()
    },
    loadMoreGroups() {
      this.startPage = this.startPage + this.paginationSize
      const totalGroups = this.filteredTreeifiedGroups.length
      this.endPage =
        this.endPage + this.paginationSize > totalGroups
          ? totalGroups
          : this.endPage + this.paginationSize
      this.updateGroupsPagination()
    }
  }
}
</script>

<style lang="scss">
// .table-wrapper {
// overflow-x: scroll;
// position: relative;
// TODO: check with many groups if this is a good solution for hiding annoying scrollbar
// overflow-y: hidden;
// }
.table-scroll-header {
  &.is-stuck {
    // background-color: var(--kt-card-bg);
    background-color: rgba(var(--kt-gray-200-rgb), 0.8);
    backdrop-filter: blur(1px);
  }
  top: 0;
  position: -webkit-sticky;
  position: sticky;
  z-index: 1;
  overflow: hidden;
  // overflow-y: hidden;
  // overflow-x: auto;
  // width: 100%;
}
.table-scroll-inner {
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
}

.table-inner {
  min-width: 1000px; /* whatever width your table needs */
}

.sticky-header-cell {
  position: sticky;
  top: 0;
  z-index: 2;
}

.tag-donuts-comparison {
  max-width: 1024px;
}
</style>
